<script setup>
  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import CardsPane from "@/components/Survey/Components/CardsList/CardsPane.vue";
  import ReviewPane from "@/components/Survey/Review/ReviewPane/ReviewPane.vue";
  import ReviewCommentsPanel from "@/components/Survey/Review/Collaboration/ReviewCommentsPanel.vue";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
  import { storeToRefs } from "pinia";
  import { useEventBus } from "@vueuse/core";
  import { useScrollToCard } from "@utils/composables/useScrollToCard";
  import { provide, nextTick, onMounted, ref } from "vue";

  const surveyCardsStore = useSurveyCardsStore();
  const { searchQuery, presenceOfCard } = storeToRefs(surveyCardsStore);
  const { selectCurrentCard, updateSearchQuery } = surveyCardsStore;

  const currentCardStore = useCurrentCardStore();
  const reviewStore = useReviewCommentsStore();

  const props = defineProps({
    cards: { type: Array, required: true },
    cardSubTypesInfos: { type: Object, required: true },
    cardTypesGroups: { type: Array, required: true },
    isCardFilteredOut: { type: Function, required: true },
    isFilterActive: { type: Boolean, required: true },
    questionCount: { type: Number, required: true },
    switchBetweenTabs: { type: Boolean, required: true },
  });

  const cardsPane = ref(null);

  const selectAndscrollToReviewCard = async ({ token }) => {
    selectCurrentCard({ token });
    reviewStore.openCardComments(token);
    await scrollToReviewCard({ token });
  };

  const scrollToReviewCard = async ({ token }) => {
    await nextTick();
    const card = document.getElementById(`review-card-${token}`);
    const reviewPaneContainer = document.getElementById("review-pane-container");
    if (card && reviewPaneContainer) {
      // Get the card's position relative to the scroll container
      const containerRect = reviewPaneContainer.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const scrollTop = reviewPaneContainer.scrollTop;
      const targetPosition = scrollTop + cardRect.top - containerRect.top - 10;

      reviewPaneContainer.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });

      card.classList.add("review-card-focused");
      window.setTimeout(() => card.classList.remove("review-card-focused"), 1500);
    }
  };

  const { currentToken } = storeToRefs(currentCardStore);
  const { scrollToCard } = useScrollToCard(cardsPane, currentToken);

  onMounted(() => {
    if (props.switchBetweenTabs) scrollToCard();
  });

  const reviewEventBus = useEventBus("review");
  provide("reviewEventBus", reviewEventBus);
  reviewEventBus.on((eventDescriptor) => {
    const { command, ...event } = eventDescriptor;
    const actions = {
      select: selectAndscrollToReviewCard,
      scroll: scrollToReviewCard,
      search: updateSearchQuery,
    };
    actions[command]?.(event);
  });
</script>

<template>
  <ThreePanePage
    :span-left="3"
    :span-center="6"
    :span-right="3"
    data-cy="survey-questions-review-page">
    <template #left>
      <CardsPane
        ref="cardsPane"
        class="h-full"
        :card-sub-types-infos="cardSubTypesInfos"
        :cards="cards"
        event-bus-name="reviewEventBus"
        :is-card-filtered-out="isCardFilteredOut"
        :is-filter-active="isFilterActive"
        :is-footer-hidden="true"
        :is-list-read-only="true"
        :question-count="questionCount"
        :search-query="searchQuery"
        :selected-card-token="currentCardStore.currentToken" />
    </template>
    <template #center>
      <div
        id="review-pane-container"
        class="h-full overflow-y-auto">
        <ReviewPane
          v-if="presenceOfCard"
          id="review-pane"
          :card-sub-types-infos="cardSubTypesInfos"
          :card-types-groups="cardTypesGroups"
          :cards="cards" />
      </div>
    </template>
    <template #right>
      <ReviewCommentsPanel v-if="presenceOfCard" />
    </template>
  </ThreePanePage>
</template>
